import type { MusicKitLike } from '../types';

/**
 * Common musickit configurations shared between apps.
 */
export class BaseMusicKitAdapter<T extends MusicKitLike, K> {
    private readonly musicKitPromise: Promise<{
        musicKit: T;
        musicKitGlobal: K;
    }>;

    private musicKitEventListeners: Array<{
        name: string;
        callback: (data?: unknown) => void;
    }> = new Array();

    constructor(musicKit: Promise<{ musicKit: T; musicKitGlobal: K }>) {
        this.musicKitPromise = musicKit;
        this.musicKitEventListeners = [];
    }

    async getMusicKit(): Promise<T> {
        const value = await this.getRejectingMusicKitPromise();
        return value.musicKit;
    }

    async getMusicKitGlobal(): Promise<K> {
        const value = await this.getRejectingMusicKitPromise();
        return value.musicKitGlobal;
    }

    async ready(): Promise<void> {
        // This should not be returned as we do not want to
        // leak the MKInstance outside of this class.
        await this.getMusicKit();
    }

    setupBeforeUnloadHandler() {
        if ('onbeforeunload' in window) {
            window.addEventListener(
                'beforeunload',
                this.handleBeforeUnload.bind(this),
            );
        }
    }

    async handleBeforeUnload(event: BeforeUnloadEvent) {
        const musickit = await this.getMusicKit();

        if (musickit.isPlaying) {
            // trigger confirmation dialog before leaving
            // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
            event.preventDefault();

            // Chrome requires returnValue to be set
            event.returnValue = '';
        }
    }

    /**
     * Proxy Method to Attach Event Listener to MusicKit
     */
    async addOneTimeEventListener(
        name: string,
        callback: (...args: unknown[]) => void,
        options: any = {},
    ) {
        const musickit = await this.getMusicKit();

        const triggerOnce = (...args: unknown[]) => {
            callback(...args);
            musickit.removeEventListener(name, triggerOnce);
        };

        musickit.addEventListener(name, triggerOnce, options);
    }

    /**
     * Proxy Method to Attach Event Listener to MusicKit
     */
    async addEventListener(
        name: string,
        callback: (data?: any) => void,
        options: any = {},
    ) {
        this.musicKitEventListeners.push({ name, callback });
        return (await this.getMusicKit()).addEventListener(
            name,
            callback,
            options,
        );
    }

    /**
     * Proxy Method to Remove Event Listener to MusicKit
     */
    async removeEventListener(name: string) {
        // TODO: this only finds the first event for the name and removes it from musickit,
        // but removes all events with that name from musicKitEventListeners
        const eventListener = this.musicKitEventListeners.find(
            (eventListener) => eventListener.name === name,
        );
        this.musicKitEventListeners = this.musicKitEventListeners.filter(
            (eventListener) => eventListener.name !== name,
        );

        if (eventListener) {
            return (await this.getMusicKit()).removeEventListener(
                name,
                eventListener.callback,
            );
        }
    }

    /**
     * Proxy Method to Remove Event Listener to MusicKit
     */
    async removeEventListenerWithCb(
        name: string,
        callback: (data?: any) => void,
    ) {
        return (await this.getMusicKit()).removeEventListener(name, callback);
    }

    private async getRejectingMusicKitPromise(): Promise<{
        musicKit: T;
        musicKitGlobal: K;
    }> {
        const value = await this.musicKitPromise;

        if (value === null) {
            throw new Error('MusicKit failed to initialize');
        }

        return value;
    }

    /**
     * Pause currently playing item.
     * @returns success A promise that rejects if pause fails
     */
    async pausePlayback<V extends Parameters<T['pause']>[0]>(
        activityMetric?: V,
    ): Promise<void> {
        //TODO: rdar://88390008 (Add system for catching events that happen before musickit is initialized)
        const musickit = await this.getMusicKit();

        const params = activityMetric || null;
        await (params ? musickit.pause(params) : musickit.pause());
    }

    /**
     * Resume playback (play without new play data).
     * @returns success A promise that rejects if resume fails
     */
    async resumePlayback(): Promise<void> {
        //TODO: rdar://88390008 (Add system for catching events that happen before musickit is initialized)
        const musickit = await this.getMusicKit();
        await musickit.play();
    }
}
